/**
 * Comprehensive Sentry Configuration for CoachSim
 * Includes error tracking, performance monitoring, user session tracking, and logging
 */

import React from 'react';
import * as Sentry from "@sentry/react";
import { ENV_CONFIG, isProduction } from "./config";
import { redactEmails } from "./secureLogger";
import { LoggedError } from "./logged-error";
// NOTE: Cannot import logger here - creates circular dependency (logger -> sentry-lazy -> sentry-config -> logger)

/**
 * CS-204/CS-211: once the production check is fixed, events actually reach
 * Sentry. Strip email addresses from every string in the event (message,
 * exception values, breadcrumbs, contexts, extra, tags) and drop the user's
 * email / IP, whatever path put them there. Mutates and returns the event.
 */
export function scrubEventPii<T>(event: T): T {
  const seen = new WeakSet<object>();
  const walk = (value: unknown, depth: number): unknown => {
    if (typeof value === 'string') return redactEmails(value);
    if (!value || typeof value !== 'object' || depth > 12) return value;
    if (seen.has(value as object)) return value;
    seen.add(value as object);
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) value[i] = walk(value[i], depth + 1);
      return value;
    }
    const rec = value as Record<string, unknown>;
    for (const key of Object.keys(rec)) rec[key] = walk(rec[key], depth + 1);
    return rec;
  };
  const e = event as unknown as { user?: Record<string, unknown> | null };
  if (e && e.user && typeof e.user === 'object') {
    delete e.user.email;
    delete e.user.ip_address;
  }
  return walk(event, 0) as T;
}

// Sentry configuration interface
interface SentryConfig {
  dsn: string;
  environment: string;
  release?: string;
  sampleRate: number;
  tracesSampleRate: number;
  profilesSampleRate: number;
  maxBreadcrumbs: number;
  debug: boolean;
  sendDefaultPii: boolean;
  attachStacktrace: boolean;
  enableLogs: boolean;
}

// Get Sentry configuration from environment
const getSentryConfig = (): SentryConfig => {
  // FIXED: Use environment variable for DSN with fallback
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    // No DSN provided - Sentry monitoring will be disabled (silent)
    return {
      dsn: '', // Empty DSN disables Sentry
      environment: import.meta.env.VITE_ENVIRONMENT || ENV_CONFIG.NODE_ENV,
      release: import.meta.env.VITE_APP_VERSION || "1.0.0",
      sampleRate: 0.0, // Disable sampling when no DSN
      tracesSampleRate: 0.0,
      profilesSampleRate: 0.0,
      maxBreadcrumbs: 50,
      debug: false,
      sendDefaultPii: false,
      attachStacktrace: true,
      enableLogs: false,
    };
  }
  
  return {
    dsn: dsn,
    environment: import.meta.env.VITE_ENVIRONMENT || ENV_CONFIG.NODE_ENV,
    release: import.meta.env.VITE_APP_VERSION || "1.0.0",
    // Production sampling, set 02-10-26 for permanent production at 50-100 users (not class-only):
    // every error is captured (error volume is low at this scale, and dropped errors hide real bugs);
    // traces at 20% for latency visibility; profiling off.
    sampleRate: 1.0,
    tracesSampleRate: isProduction() ? 0.2 : 0.05,
    profilesSampleRate: 0.0,
    maxBreadcrumbs: 50,

    // Enable debug only when explicitly requested via VITE_SENTRY_DEBUG
    debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',
    sendDefaultPii: false,
    attachStacktrace: true,
    enableLogs: true, // Enable logs for development debugging
  };
};

// Custom tags for better error categorization
const getCustomTags = () => ({
  component: 'unknown',
  feature: 'unknown',
  user_tier: 'unknown',
  session_type: 'unknown',
});

// Track whether Sentry has been initialized in this page load
let sentryInitialized = false;

/** Returns true if Sentry.init() has been called and not yet shut down. */
export const isSentryInitialized = () => sentryInitialized;

// Initialize Sentry with comprehensive configuration
export const initializeSentry = () => {
  const config = getSentryConfig();
  const enableReplay = isProduction() && import.meta.env.VITE_SENTRY_REPLAY === 'true';
  const enableFeedback = isProduction() && import.meta.env.VITE_SENTRY_FEEDBACK === 'true';
  
  // Don't initialize if no DSN is provided
  if (!config.dsn) {
    // Silent return - no Sentry monitoring when DSN missing
    return;
  }
  
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    
    // Sampling configuration
    sampleRate: config.sampleRate,
    tracesSampleRate: config.tracesSampleRate,
    profilesSampleRate: config.profilesSampleRate,
    // Replay sampling is fully gated by flags in production. Never record ordinary sessions
    // (coaching conversations); record only sessions that hit an error, with all text and media masked.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: enableReplay ? 1.0 : 0.0,
    
    // Error handling configuration
    maxBreadcrumbs: config.maxBreadcrumbs,
    debug: config.debug,
    sendDefaultPii: config.sendDefaultPii,
    attachStacktrace: config.attachStacktrace,
    
    // Enable experimental features
    _experiments: {
      enableLogs: config.enableLogs,
    },
    
    // Integrations
    integrations: [
      // FIXED: Add console logging integration for development
      ...(config.debug ? [Sentry.consoleLoggingIntegration({ 
        levels: ["error", "warn", "info", "debug"] // Enable all levels in development
      })] : []),
      
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      
      // Browser profiling - only enable in production
      ...(isProduction() ? [Sentry.browserProfilingIntegration()] : []),
      
      // User feedback integration - gated by flag in production
      ...(enableFeedback ? [Sentry.feedbackIntegration({
        colorScheme: "system",
        showBranding: false,
      })] : []),
      
      // Session replay for debugging - gated by flag in production
      ...(enableReplay ? [Sentry.replayIntegration({
        maskAllText: true, // Mask sensitive text
        blockAllMedia: true, // Block sensitive media
      })] : []),
    ],
    
    // Performance monitoring
    beforeSendTransaction: (transaction) => {
      // Filter out unimportant transactions and limit data collection
      if (transaction.transaction?.includes('static/') || 
          transaction.transaction?.includes('favicon') ||
          transaction.transaction?.includes('robots.txt')) {
        return null;
      }
      
      return transaction;
    },
    
    // Error filtering and enhancement
    beforeSend: (event, hint) => {
      // CS-211: no email addresses leave the browser, wherever they appear.
      scrubEventPii(event);

      // SECURITY FIX: Enhanced data scrubbing to prevent PII exposure
      if (event.request && (event.request as any).data) {
        // Scrub sensitive fields from request data
        const sensitiveFields = ['password', 'token', 'apiKey', 'secret', 'authorization', 'cookie'] as const;
        const reqData = (event.request as any).data as Record<string, unknown> | undefined;
        if (reqData) {
          sensitiveFields.forEach((field) => {
            if (field in reqData) {
              (reqData as Record<string, unknown>)[field] = '[REDACTED]';
            }
          });
        }
      }
      
      // Scrub sensitive headers
      if (event.request && (event.request as any).headers) {
        const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key', 'x-auth-token'] as const;
        const headers = (event.request as any).headers as Record<string, unknown> | undefined;
        if (headers) {
          sensitiveHeaders.forEach((header) => {
            if (header in headers) {
              (headers as Record<string, unknown>)[header] = '[REDACTED]';
            }
          });
        }
      }
      
      // Scrub sensitive URL parameters
      if (event.request && (event.request as any).url) {
        const url = new URL(((event.request as any).url as string) || '');
        const sensitiveParams = ['token', 'api_key', 'password', 'secret'];
        sensitiveParams.forEach(param => {
          if (url.searchParams.has(param)) {
            url.searchParams.set(param, '[REDACTED]');
          }
        });
        (event.request as any).url = url.toString();
      }
      
      // Don't send events in development unless explicitly enabled
      if (!isProduction() && !import.meta.env.VITE_SENTRY_DEBUG) {
        // Silent discard - dev events not sent unless VITE_SENTRY_DEBUG=true
        return null;
      }
      
      // Filter out known non-critical errors
      const error = hint.originalException;
      if (error instanceof Error) {
        const message = error.message.toLowerCase();
        
        // Filter out common browser extension errors
        if (message.includes('non-error promise rejection') ||
            message.includes('script error') ||
            message.includes('network error') ||
            message.includes('loading css chunk')) {
          return null;
        }
        
        // Enhance error context
        if (event.exception?.values?.[0]) {
          const exception = event.exception.values[0];
          
          // Add custom error categorization
          if (message.includes('auth') || message.includes('login')) {
            event.tags = { ...event.tags, category: 'authentication' };
          } else if (message.includes('api') || message.includes('fetch')) {
            event.tags = { ...event.tags, category: 'api_error' };
          } else if (message.includes('ui') || message.includes('component')) {
            event.tags = { ...event.tags, category: 'ui_error' };
          }
        }
      }
      
      return event;
    },
    
    // Initial tags
    initialScope: {
      tags: getCustomTags(),
    },
  });
  
  // Set user context if available
  const user = getCurrentUser();
  if (user) {
    setSentryUser(user);
  }

  sentryInitialized = true;
};

/** Flush pending events and shut down the Sentry client. */
export async function shutdownSentry(): Promise<void> {
  if (!sentryInitialized) return;
  await Sentry.close(2000); // 2s flush timeout
  sentryInitialized = false;
}

// User context management
interface UserContext {
  id?: string;
  email?: string;
  subscription_tier?: string;
  is_dev_user?: boolean;
}

export const setSentryUser = (user: UserContext) => {
  Sentry.setUser({
    id: user.id,
    // CS-211: email deliberately not sent; the user id is enough to correlate.
    subscription_tier: user.subscription_tier,
    is_dev_user: user.is_dev_user,
  });
  
  // Update tags based on user
  Sentry.setTag('user_tier', user.subscription_tier || 'unknown');
  Sentry.setTag('is_dev_user', user.is_dev_user ? 'true' : 'false');
};

export const clearSentryUser = () => {
  Sentry.setUser(null);
  Sentry.setTag('user_tier', 'unknown');
  Sentry.setTag('is_dev_user', 'false');
};

// Session tracking
export const startSentrySession = (sessionType: 'coaching' | 'reflection' | 'dashboard') => {
  Sentry.setTag('session_type', sessionType);
  Sentry.addBreadcrumb({
    message: `Started ${sessionType} session`,
    category: 'session',
    level: 'info',
    timestamp: Date.now() / 1000,
  });
};

export const endSentrySession = (sessionType: string, duration?: number) => {
  Sentry.addBreadcrumb({
    message: `Ended ${sessionType} session`,
    category: 'session',
    level: 'info',
    data: { duration_ms: duration },
    timestamp: Date.now() / 1000,
  });
  
  // Clear session-specific tags
  Sentry.setTag('session_type', 'unknown');
};

// Performance monitoring helpers
export const trackUserAction = (actionName: string, data?: Record<string, unknown>) => {
  return Sentry.startSpan(
    {
      op: "ui.action",
      name: actionName,
    },
    (span) => {
      if (data) {
        Object.entries(data).forEach(([key, value]) => {
          span.setAttribute(key, String(value));
        });
      }
      
      Sentry.addBreadcrumb({
        message: `User action: ${actionName}`,
        category: 'user',
        level: 'info',
        data,
        timestamp: Date.now() / 1000,
      });
    }
  );
};

export const trackAPICall = async <T>(
  url: string,
  operation: () => Promise<T>,
  method: string = 'GET'
): Promise<T> => {
  return Sentry.startSpan(
    {
      op: "http.client",
      name: `${method} ${url}`,
    },
    async (span) => {
      span.setAttribute("http.method", method);
      span.setAttribute("http.url", url);
      
      try {
        const result = await operation();
        span.setAttribute("http.status_code", "200");
        return result;
      } catch (error) {
        span.setAttribute("http.status_code", "error");
        span.setAttribute("error", true);
        throw error;
      }
    }
  );
};

// Logging helpers
type SentryLevel = 'fatal' | 'error' | 'warning' | 'info' | 'debug' | 'warn';

type EventLevel = Exclude<SentryLevel, 'warn'>;

/** Only these levels become Sentry issues; everything below is a breadcrumb. */
export function isIssueLevel(level: EventLevel): boolean {
  return level === 'error' || level === 'fatal';
}

/**
 * Logger → Sentry. 03-10 production: one new user's first session raised
 * five Sentry issues, 37 of the 49 events being info lines (auth_refocus_start,
 * auth_refocus_success) sent as issues, and the error lines carried no error
 * detail. Now: error/fatal → captureException with the original error (or a
 * LoggedError with a stack); warning/info/debug/trace → breadcrumbs only, so
 * they show up as context on the next real issue.
 */
function sentryInternalLog(level: SentryLevel | 'trace', message: string, extra?: Record<string, unknown>, error?: Error) {
  const lvl: EventLevel = level === 'trace' ? 'debug' : (level === 'warn' ? 'warning' : (level as EventLevel));
  const hasExtra = !!extra && Object.keys(extra).length > 0;
  if (!isIssueLevel(lvl)) {
    Sentry.addBreadcrumb({ category: 'log', level: lvl, message, data: hasExtra ? extra : undefined });
    return;
  }
  Sentry.withScope((scope) => {
    scope.setLevel(lvl);
    scope.setExtra('log_message', message);
    if (hasExtra) scope.setContext('log_extra', extra as Record<string, unknown>);
    // A real error groups by its own type and stack, split by what was logged.
    if (error && !(error instanceof LoggedError)) scope.setFingerprint(['{{ default }}', message]);
    Sentry.captureException(error ?? new LoggedError(message));
  });
}

export const sentryLogger = {
  trace: (message: string, extra?: Record<string, unknown>) => sentryInternalLog('trace', message, extra),
  debug: (message: string, extra?: Record<string, unknown>) => sentryInternalLog('debug', message, extra),
  info: (message: string, extra?: Record<string, unknown>) => sentryInternalLog('info', message, extra),
  warn: (message: string, extra?: Record<string, unknown>) => sentryInternalLog('warning', message, extra),
  error: (message: string, extra?: Record<string, unknown>, error?: Error) => sentryInternalLog('error', message, extra, error),
  fatal: (message: string, extra?: Record<string, unknown>, error?: Error) => sentryInternalLog('fatal', message, extra, error),
  fmt: (template: TemplateStringsArray, ...values: unknown[]) => String.raw(template, ...values),
};

// Error capturing helpers
export const captureError = (error: Error, context?: {
  component?: string;
  feature?: string;
  user_action?: string;
  extra?: Record<string, unknown>;
}) => {
  Sentry.withScope((scope) => {
    if (context) {
      if (context.component) scope.setTag('component', context.component);
      if (context.feature) scope.setTag('feature', context.feature);
      if (context.user_action) scope.setTag('user_action', context.user_action);
      if (context.extra) scope.setContext('additional_info', context.extra);
    }
    
    Sentry.captureException(error);
  });
};

export const captureMessage = (
  message: string, 
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  context?: Record<string, unknown>
) => {
  // Telemetry markers (auth_refocus_start/success/failure, ...) are context,
  // not issues: below error level they are breadcrumbs.
  if (!isIssueLevel(level)) {
    Sentry.addBreadcrumb({ category: 'telemetry', level, message, data: context });
    return;
  }
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setTag(key, String(value));
      });
    }

    Sentry.captureMessage(message, level);
  });
};

// Component error boundary integration
export const setSentryErrorBoundaryComponent = (componentName: string) => {
  Sentry.setTag('error_boundary', componentName);
};

// Get current user (placeholder - integrate with your auth system)
const getCurrentUser = (): UserContext | null => {
  try {
    // This should integrate with your actual auth system
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

// Export Sentry for direct use
export { Sentry };
